import { useForm } from '@tanstack/react-form';

import { FormDialog } from '@/components/composed/FormDialog';
import { FormField } from '@/components/composed/FormField';
import { fieldErrorMessages } from '@/lib/forms/field-errors';
import { Input } from '@/components/ui/input';
import { notify } from '@/components/composed/notify';
import { ApiError } from '@/lib/api/errors';
import {
  entityContractFormSchema,
  toEntityContractInput,
  type EntityContractFormValues,
} from '../schemas/entity-contract-form';
import { useSendEntityContract } from '../api/entity-contract-mutations';

/*
 * The prototype's "send contract" flow. Named for the domain verb (send), it
 * composes the same FormDialog + FormField primitives the create dialogs use.
 * The `.docx` document upload the prototype modal collects is a BFF concern
 * layered on later (see review.md); this dialog captures the JSON fields the
 * FE-owned contract models.
 */
export function SendContractDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const sendContract = useSendEntityContract();

  const form = useForm({
    defaultValues: {
      partnerName: '',
      relationshipType: '',
      effectiveDate: '',
    } as EntityContractFormValues,
    validators: {
      onChange: entityContractFormSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          await sendContract.mutateAsync(toEntityContractInput(value));
          return null;
        } catch (error) {
          if (error instanceof ApiError && error.fieldErrors?.length) {
            const fields: Record<string, string> = {};
            for (const fe of error.fieldErrors) fields[fe.field] = fe.message;
            return { fields };
          }
          return {
            form:
              error instanceof ApiError
                ? error.message
                : 'Failed to send contract.',
          };
        }
      },
    },
    onSubmit: () => {
      notify.success('Contract sent.');
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Send Contract"
      onSubmit={() => void form.handleSubmit()}
      submitLabel="Send"
      pending={sendContract.isPending}
    >
      <form.Field name="partnerName">
        {(field) => (
          <FormField
            label="Partner Name"
            errors={fieldErrorMessages(field.state.meta.errors)}
          >
            {({ id }) => (
              <Input
                id={id}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </FormField>
        )}
      </form.Field>

      <form.Field name="relationshipType">
        {(field) => (
          <FormField
            label="Relationship Type"
            errors={fieldErrorMessages(field.state.meta.errors)}
          >
            {({ id }) => (
              <Input
                id={id}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </FormField>
        )}
      </form.Field>

      <form.Field name="effectiveDate">
        {(field) => (
          <FormField
            label="Effective Date"
            errors={fieldErrorMessages(field.state.meta.errors)}
          >
            {({ id }) => (
              <Input
                id={id}
                type="date"
                value={field.state.value ?? ''}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </FormField>
        )}
      </form.Field>
    </FormDialog>
  );
}
